import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from "@/hooks/ui/use-toast";
import { useRoles } from '@/hooks/employees/useRoles';
import { useEmploymentTypes } from '@/hooks/compliance/useEmploymentTypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';

interface EditEmployeeFormProps {
  employee: Employee | null; // Employee being edited
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onEmployeeUpdated: (updatedEmployee: Employee) => void;
}

const EditEmployeeForm: React.FC<EditEmployeeFormProps> = ({
  employee,
  isOpen,
  onOpenChange,
  onEmployeeUpdated,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [employmentTypeId, setEmploymentTypeId] = useState<string | null>(null);
  const [salaryAdjustment, setSalaryAdjustment] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch roles
  const { data: roles } = useRoles(employee?.business_id || '');

  // Fetch employment types for the business
  const { employmentTypes, isLoading: loadingTypes } = useEmploymentTypes(employee?.business_id || '');

  useEffect(() => {
    // Pre-fill form when employee data is available and modal opens
    if (employee && isOpen) {
      setName(employee.name);
      setEmail(employee.email);
      setRoleId(employee.role_id || null);
      setEmploymentTypeId(employee.employment_type_id || null);
      setSalaryAdjustment(employee.salary_adjustment || null);
    }
    // Reset form when modal closes
    if (!isOpen) {
        setName('');
        setEmail('');
        setRoleId(null);
        setEmploymentTypeId(null);
        setSalaryAdjustment(null);
    }
  }, [employee, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee) return; // Should not happen if modal is open correctly

    setIsSubmitting(true);

    if (!name.trim() || !email.trim()) {
      toast.error('Name and Email are required.');
      setIsSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('employees')
        .update({
          name,
          email,
          role_id: roleId,
          employment_type_id: employmentTypeId,
          salary_adjustment: salaryAdjustment
        })
        .eq('id', employee.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        toast.success('Employee updated successfully!');
        onEmployeeUpdated(data as Employee); // Pass the updated employee data
        onOpenChange(false); // Close the dialog
      }
    } catch (error: unknown) {
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee', {
        description: error instanceof Error ? error.message : String(error) || 'An unexpected error occurred.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render the dialog if no employee is selected
  if (!employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Employee</DialogTitle>
          <DialogDescription>
            Update the employee's details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <Label htmlFor="edit-employee-name">Name</Label>
            <Input
              id="edit-employee-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>
          <div>
            <Label htmlFor="edit-employee-email">Email</Label>
            <Input
              id="edit-employee-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john.doe@example.com"
              required
            />
          </div>
          <div>
            <Label htmlFor="edit-employee-role-id">Role</Label>
            <Select value={roleId || 'none'} onValueChange={(value) => setRoleId(value === 'none' ? null : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No role assigned</SelectItem>
                {roles?.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                    {role.default_pay_rate && (
                      <span className="text-sm text-gray-500 ml-2">
                        (${role.default_pay_rate.toFixed(2)}{role.pay_type === 'hourly' ? '/hr' : role.pay_type === 'salary' ? '/yr' : ''})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Employment Type */}
          <div>
            <Separator className="my-4" />
            <Label htmlFor="edit-employment-type">Employment Type</Label>
            <Select
              value={employmentTypeId || 'none'}
              onValueChange={(value) => setEmploymentTypeId(value === 'none' ? null : value)}
              disabled={loadingTypes}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingTypes ? "Loading..." : "Select employment type"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {employmentTypes?.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      <span>{type.label_native}</span>
                      <Badge variant="outline" className="text-xs">
                        {type.code}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {employmentTypeId && (
              <p className="text-sm text-muted-foreground mt-1">
                {employmentTypes?.find(t => t.id === employmentTypeId)?.label_en}
              </p>
            )}
            <Separator className="my-4" />
          </div>
          
          <div>
            <Label htmlFor="edit-salary-adjustment">Salary Adjustment (Optional)</Label>
            <Input
              id="edit-salary-adjustment"
              type="number"
              step="0.01"
              value={salaryAdjustment || ''}
              onChange={(e) => setSalaryAdjustment(e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="e.g., 2.50 (adjustment to base rate)"
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditEmployeeForm;
